import React from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import styled from 'styled-components';
import { BodyRegular } from '../../atoms/fonts/Body';
import { GenericDropDownList } from '../../molecules/GenericDropDownList';
import { TIMEZONES } from '@shared/utils/Timezones';
import { ReportSchedule } from '@shared/models/report.models';

// ISO day-of-week ids (1 = Monday .. 7 = Sunday) to match the backend.
const DAYS_OF_WEEK = [1, 2, 3, 4, 5, 6, 7].map(day => ({ id: day, name: moment().isoWeekday(day).format('dddd') }));

const SEND_TIMES = Array.from({ length: 24 }, (_, hour) => {
  const label = `${hour.toString().padStart(2, '0')}:00`;
  return { id: label, name: label };
});

/**
 * Day / time / timezone pickers shared by every weekly automated report, laid out on one line as
 * "Send on [day] at [time] [timezone]". Purely presentational: the owning
 * setting keeps the state and persists on each change.
 */
export function ReportScheduleSetting(props: Props) {
  const { schedule, disabled, noteKey, onChange } = props;
  const { t } = useTranslation();

  return (
    <>
      <BodyRegular weight={600} style={{ marginTop: 20, marginBottom: 4 }}>{t('settings.reports-schedule.label')}</BodyRegular>
      <BodyRegular style={{ marginBottom: 12 }}>
        {t('settings.reports-schedule.description')}{noteKey ? ` ${t(noteKey)}` : ''}
      </BodyRegular>

      <InlineRow>
        <BodyRegular weight={600}>{t('settings.reports-schedule.day')}</BodyRegular>
        <GenericDropDownList className={'dropdown dropdown--day'}
                             disabled={disabled}
                             items={DAYS_OF_WEEK}
                             placeholder={'settings.reports-schedule.day'}
                             selectedId={schedule.sendDay}
                             onSelect={(id: any) => onChange({ ...schedule, sendDay: parseInt(id) })} />
        <BodyRegular weight={600}>{t('settings.reports-schedule.time')}</BodyRegular>
        <GenericDropDownList className={'dropdown dropdown--time'}
                             disabled={disabled}
                             items={SEND_TIMES}
                             placeholder={'settings.reports-schedule.time'}
                             selectedId={schedule.sendTime}
                             onSelect={(id: any) => onChange({ ...schedule, sendTime: id })} />
        <GenericDropDownList className={'dropdown dropdown--timezone'}
                             disabled={disabled}
                             items={TIMEZONES}
                             placeholder={'settings.reports-schedule.timezone'}
                             selectedId={schedule.timezoneId}
                             onSelect={(id: any) => onChange({ ...schedule, timezoneId: id })} />
      </InlineRow>
    </>
  );
}

interface Props {
  schedule: ReportSchedule;
  disabled?: boolean;
  /** Optional i18n key for a one-line note on which week the report covers given the send day. */
  noteKey?: string;
  onChange: (schedule: ReportSchedule) => void;
}

const InlineRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 12px;

  .dropdown {
    margin: 0;
    width: auto;
    min-width: 0;
  }
  .dropdown--day {
    min-width: 160px;
  }
  .dropdown--time {
    min-width: 110px;
  }
  .dropdown--timezone {
    flex: 1;
    min-width: 240px;
  }
`
